import { useMutation, useQuery } from "@apollo/client/react"
import { Download, Film } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { gql } from "~/__generated__"
import { postPath } from "~/common/paths"
import { Button } from "~/ui/button"
import { GraphqlError } from "~/ui/graphql-error"
import { CleanupPanel } from "./cleanup-panel"

const CLIPS = gql(/* GraphQL */ `
  query PostClips($id: ID!) {
    editorialWorkspace {
      reapConfigured
      post(id: $id) {
        id
        editable
        clipRuns {
          id
          status
          error
          duration
          sourceCurrent
          cleanupVersion
          cleanupMode
          createdAt
          socialClips {
            id
            title
            duration
            captioned
            videoUrl
            contentPostId
          }
        }
        socialClip {
          parentPostId
          id
          title
          duration
          captioned
          videoUrl
        }
      }
    }
  }
`)
const CREATE = gql(/* GraphQL */ `
  mutation CreatePostClips($input: ClipsCreateInput!) {
    clipsCreate(input: $input) {
      run {
        id
        status
      }
    }
  }
`)
const RETRY = gql(/* GraphQL */ `
  mutation RetryPostClips($input: ClipsRetryInput!) {
    clipsRetry(input: $input) {
      run {
        id
        status
      }
    }
  }
`)

const cleanupLabels: Record<string, string> = {
  natural: "Natural",
  tight: "Tight",
  very_tight: "Very tight",
}

const phaseLabels: Record<string, string> = {
  queued: "Clip creation queued",
  preparing: "Assembling your reaction",
  uploading: "Sending your recording to Reap",
  submitting: "Starting clip creation",
  processing: "Finding and captioning your clips",
  saving: "Saving your clip videos",
  failed: "Clip creation needs attention",
  uncertain: "Checking a submission needs attention",
}

export function ClipsPanel({ postId, hasRecordings }: { postId: string; hasRecordings: boolean }) {
  const { data, error, refetch } = useQuery(CLIPS, {
    variables: { id: postId },
    pollInterval: 5000,
  })
  const [create, creation] = useMutation(CREATE)
  const [retry, retrying] = useMutation(RETRY)
  const [actionError, setError] = useState<Error | null>(null)
  const post = data?.editorialWorkspace.post
  const runs = post?.clipRuns ?? []
  const busy = runs.some((run) => !["ready", "failed", "uncertain"].includes(run.status))
  const clip = post?.socialClip
  return (
    <section id="social-clips" className="rounded-2xl border bg-card p-6" aria-label="Social clips">
      <div className="flex items-center gap-2">
        <Film className="size-5 text-primary" />
        <h3 className="text-xl font-semibold">{clip ? "Clip video" : "Create social clips"}</h3>
      </div>
      {clip ? (
        <>
          {clip.videoUrl && (
            <>
              <video
                controls
                playsInline
                preload="metadata"
                src={clip.videoUrl}
                className="mt-5 max-h-96 w-full rounded-xl bg-black"
              />
              <a
                className="mt-3 inline-flex items-center gap-2 text-sm underline"
                href={`${clip.videoUrl}?download=1`}
              >
                <Download className="size-4" />
                Download clip
              </a>
            </>
          )}
          <Link
            className="mt-3 block text-sm underline"
            to={postPath({ postId: clip.parentPostId })}
          >
            Back to the full reaction
          </Link>
          <p className="mt-3 text-sm text-muted-foreground">
            Write and approve this clip’s LinkedIn feed text in the editor. Downloading does not
            mark it posted.
          </p>
        </>
      ) : (
        <>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            Combine your included sections and let Reap find complete thoughts for captioned
            portrait clips. At least 2 minutes of footage is required. Originals stay saved;
            exported clips get their own editable posts.
          </p>
          {post?.editable && (
            <CleanupPanel
              postId={postId}
              hasRecordings={hasRecordings}
              clipBusy={busy || creation.loading}
              reapConfigured={!!data?.editorialWorkspace.reapConfigured}
              onCreateClips={async (cleanupId, cleanupVersion) => {
                const result = await create({
                  variables: { input: { postId, cleanupId, cleanupVersion } },
                })
                if (result.error) throw result.error
                await refetch()
              }}
            />
          )}
          {post?.editable && (
            <Button
              aria-label="Create clips from included sections"
              variant="outline"
              className="mt-4 h-auto whitespace-normal py-2 text-left"
              disabled={
                !hasRecordings ||
                busy ||
                creation.loading ||
                !data?.editorialWorkspace.reapConfigured
              }
              onClick={async () => {
                setError(null)
                try {
                  const result = await create({ variables: { input: { postId } } })
                  if (result.error) throw result.error
                  await refetch()
                } catch (err) {
                  setError(err instanceof Error ? err : new Error("Could not start clipping"))
                }
              }}
            >
              {busy
                ? "Clips are processing…"
                : "Use original · Create clips from included sections"}
            </Button>
          )}
          {data && !data.editorialWorkspace.reapConfigured && (
            <p className="mt-3 text-sm text-muted-foreground">
              Reap needs to be connected before creating clips.
            </p>
          )}
          {runs.map((run) => (
            <div key={run.id} className="mt-5 rounded-xl border p-4">
              <p className="font-medium">
                {run.status === "ready"
                  ? `${run.socialClips.length} clips saved`
                  : (phaseLabels[run.status] ?? "Creating clips")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {run.cleanupMode
                  ? `${cleanupLabels[run.cleanupMode] ?? run.cleanupMode} cleanup${run.cleanupVersion != null ? ` · preview v${run.cleanupVersion}` : ""}`
                  : "Original recording"}
              </p>
              {!run.sourceCurrent && (
                <p className="mt-2 text-sm text-muted-foreground">
                  This batch uses an earlier selection of sections. Create a new batch to use your
                  current selection.
                </p>
              )}
              {run.error && (
                <p role="alert" className="mt-2 text-sm text-destructive">
                  {run.error}
                </p>
              )}
              {run.status === "failed" && post?.editable && (
                <Button
                  variant="outline"
                  className="mt-3"
                  disabled={retrying.loading}
                  onClick={async () => {
                    setError(null)
                    try {
                      const result = await retry({ variables: { input: { id: run.id } } })
                      if (result.error) throw result.error
                      await refetch()
                    } catch (err) {
                      setError(err instanceof Error ? err : new Error("Could not retry clipping"))
                    }
                  }}
                >
                  Retry / check progress
                </Button>
              )}
              <div className="mt-4 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {run.socialClips.map((item) => (
                  <article className="rounded-lg border p-3" key={item.id}>
                    {item.videoUrl && (
                      <video
                        src={item.videoUrl}
                        controls
                        playsInline
                        preload="metadata"
                        className="aspect-[9/16] max-h-80 w-full rounded-lg bg-black"
                      />
                    )}
                    <h4 className="mt-3 font-medium">{item.title}</h4>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {Math.round(item.duration ?? 0)} seconds ·{" "}
                      {item.captioned ? "Captioned" : "Check captions before posting"}
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3 text-sm">
                      {item.videoUrl && (
                        <a href={`${item.videoUrl}?download=1`} className="underline">
                          Download video
                        </a>
                      )}
                      {item.contentPostId && (
                        <Link className="underline" to={postPath({ postId: item.contentPostId })}>
                          Edit clip post
                        </Link>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
      {(error || actionError) && (
        <div className="mt-4">
          <GraphqlError error={(actionError ?? error)!} />
        </div>
      )}
    </section>
  )
}
